import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion } from 'framer-motion';
import {
    PencilSquareIcon,
    AtSymbolIcon,
    PhoneIcon,
    MapPinIcon,
    GlobeAltIcon,
    ChatBubbleBottomCenterTextIcon,
    PlusIcon
} from '@heroicons/react/24/outline';
import { API } from '../../config';

const AdminFooter = () => {
    const [settings, setSettings] = useState({
        tagline: '',
        description: '',
        email: '',
        phone: '',
        location: '',
        copyright: '',
        facebook: '',
        instagram: '',
        linkedin: ''
    });
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchSettings();
        fetchCategories();
    }, []);

    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${API}/categories`);
            setCategories(response.data);
        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${API}/footer`);
            setSettings(response.data);
        } catch (error) {
            console.error('Failed to fetch footer settings:', error);
            toast.error('Failed to load footer settings');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/footer`, settings, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Footer updated successfully!');
            setIsEditing(false);
            fetchSettings();
        } catch (error) {
            toast.error('Failed to update footer');
        } finally {
            setLoading(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm overflow-hidden"
        >
            <div className="p-4 lg:p-8">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h2 className="text-xl lg:text-2xl font-bold text-gray-900">Footer Settings</h2>
                        <p className="text-gray-500 text-sm mt-1">Manage the content and contact info in your website footer</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8 max-w-4xl">
                    {/* Brand Info */}
                    <div className="grid grid-cols-1 gap-6 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center">
                            <ChatBubbleBottomCenterTextIcon className="h-4 w-4 mr-2" />
                            Brand Identity
                        </h3>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Tagline / Services</label>
                            <input
                                type="text"
                                value={settings.tagline}
                                onChange={e => setSettings({ ...settings, tagline: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                placeholder="e.g., Creative Designs • Printing • Digital Assets"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Brand Description</label>
                            <textarea
                                value={settings.description}
                                onChange={e => setSettings({ ...settings, description: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                placeholder="Detail what your business does..."
                                rows={3}
                            />
                        </div>
                    </div>

                    {/* Contact Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center col-span-full">
                            <PhoneIcon className="h-4 w-4 mr-2" />
                            Contact Information
                        </h3>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Contact Email</label>
                            <div className="relative">
                                <AtSymbolIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="email"
                                    value={settings.email}
                                    onChange={e => setSettings({ ...settings, email: e.target.value })}
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                    placeholder="your-email@gmail.com"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Contact Phone</label>
                            <div className="relative">
                                <PhoneIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                                <input
                                    type="text"
                                    value={settings.phone}
                                    onChange={e => setSettings({ ...settings, phone: e.target.value })}
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                    placeholder="+91 12345 67890"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-full">
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Physical Address</label>
                            <div className="relative">
                                <MapPinIcon className="h-4 w-4 text-gray-400 absolute left-3 top-2.5" />
                                <textarea
                                    value={settings.location}
                                    onChange={e => setSettings({ ...settings, location: e.target.value })}
                                    className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                    placeholder="Full street address..."
                                    rows={2}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Links & Social */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6 bg-gray-50 rounded-2xl border border-gray-100">
                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center col-span-full">
                            <GlobeAltIcon className="h-4 w-4 mr-2" />
                            Quick Links
                        </h3>
                        <div className="col-span-full space-y-3">
                            {settings.links?.map((link, idx) => (
                                <div key={idx} className="flex gap-3">
                                    <input
                                        type="text"
                                        value={link.label}
                                        onChange={e => {
                                            const newLinks = [...settings.links];
                                            newLinks[idx].label = e.target.value;
                                            setSettings({ ...settings, links: newLinks });
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm"
                                        placeholder="Link Label"
                                    />
                                    <select
                                        value={
                                            ['/', '/products', '/cart', '/profile'].includes(link.url) ||
                                                categories.some(c => `/products?category_id=${c.id}` === link.url)
                                                ? link.url : (link.url ? 'custom' : '')
                                        }
                                        onChange={e => {
                                            const val = e.target.value;
                                            const newLinks = [...settings.links];
                                            if (val === 'custom') {
                                                newLinks[idx].url = '';
                                            } else {
                                                newLinks[idx].url = val;
                                                // Auto-fill label if empty
                                                if (!newLinks[idx].label || newLinks[idx].label === 'Select Page/Category') {
                                                    const selectedOption = e.target.options[e.target.selectedIndex];
                                                    newLinks[idx].label = selectedOption.text;
                                                }
                                            }
                                            setSettings({ ...settings, links: newLinks });
                                        }}
                                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm bg-white"
                                    >
                                        <option value="">Select Page/Category</option>
                                        <optgroup label="Main Pages">
                                            <option value="/">Home</option>
                                            <option value="/products">All Collections</option>
                                            <option value="/cart">Shopping Cart</option>
                                            <option value="/profile">User Profile</option>
                                        </optgroup>
                                        <optgroup label="Categories">
                                            {categories.map(cat => (
                                                <option key={cat.id} value={`/products?category_id=${cat.id}`}>
                                                    {cat.name}
                                                </option>
                                            ))}
                                        </optgroup>
                                        <optgroup label="Extra">
                                            <option value="custom">-- Custom URL --</option>
                                        </optgroup>
                                    </select>

                                    {/* Show input if URL is custom or doesn't match predefined ones */}
                                    {(!['/', '/products', '/cart', '/profile'].includes(link.url) &&
                                        !categories.some(c => `/products?category_id=${c.id}` === link.url)) && (
                                            <input
                                                type="text"
                                                value={link.url}
                                                onChange={e => {
                                                    const newLinks = [...settings.links];
                                                    newLinks[idx].url = e.target.value;
                                                    setSettings({ ...settings, links: newLinks });
                                                }}
                                                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm"
                                                placeholder="Enter Custom URL (e.g. /contact)"
                                            />
                                        )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newLinks = settings.links.filter((_, i) => i !== idx);
                                            setSettings({ ...settings, links: newLinks });
                                        }}
                                        className="text-red-500 hover:text-red-700 px-2"
                                    >
                                        Remove
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setSettings({ ...settings, links: [...(settings.links || []), { label: '', url: '' }] })}
                                className="text-sm font-bold text-[#E31E24] flex items-center gap-1"
                            >
                                <PlusIcon className="h-4 w-4" /> Add Link
                            </button>
                        </div>

                        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide flex items-center col-span-full mt-4">
                            <GlobeAltIcon className="h-4 w-4 mr-2" />
                            Social Links & Footer Bottom
                        </h3>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Instagram URL</label>
                            <input
                                type="url"
                                value={settings.instagram || ''}
                                onChange={e => setSettings({ ...settings, instagram: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                placeholder="https://instagram.com/..."
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Facebook URL</label>
                            <input
                                type="url"
                                value={settings.facebook || ''}
                                onChange={e => setSettings({ ...settings, facebook: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                placeholder="https://facebook.com/..."
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">LinkedIn URL</label>
                            <input
                                type="url"
                                value={settings.linkedin || ''}
                                onChange={e => setSettings({ ...settings, linkedin: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                placeholder="https://linkedin.com/in/..."
                            />
                        </div>
                        <div className="md:col-span-full mt-4">
                            <label className="block text-xs font-semibold text-gray-700 mb-1.5 uppercase tracking-wide">Copyright Text</label>
                            <input
                                type="text"
                                value={settings.copyright}
                                onChange={e => setSettings({ ...settings, copyright: e.target.value })}
                                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-900 focus:border-gray-900 bg-white text-sm"
                                placeholder="© 2025 SR Daily Nutrition. All rights reserved."
                            />
                        </div>
                    </div>

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-gray-900 text-white px-8 py-3 rounded-xl hover:bg-black transition-all shadow-lg text-sm font-bold disabled:opacity-50 flex items-center"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Updating...
                                </>
                            ) : (
                                <>
                                    <PencilSquareIcon className="h-4 w-4 mr-2" />
                                    Save Footer Settings
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </motion.div>
    );
};

export default AdminFooter;
